import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { ApiClient } from '../../services/apiClient';
import { Row, Col, Card, Statistic, Tag, Spin, Alert, Typography, Table, Tooltip } from 'antd';
import {
  SyncOutlined,
  CloudDownloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import PageHelp from '../../components/PageHelp';
import { formatAwsSchedule, formatTimeUntil, formatUtcDateTime } from '../../utils/cronFormat';

const { Title } = Typography;

type SyncInfo = {
  status: string;
  started_at: string;
  completed_at: string;
  [key: string]: unknown;
};

type LambdaInfo = {
  name: string;
  runtime: string;
  memory: number;
  timeout: number;
  lastModified: string;
  authzEnabled: string;
  sqlLogging: string;
  corsOrigins: string;
};

type ScheduleInfo = {
  name: string;
  schedule: string;
  state: string;
  description: string;
};

type ServiceConfig = {
  lambdas: LambdaInfo[];
  schedules: ScheduleInfo[];
  features: { environment: string; authzEnabled: boolean; sqlLogging: boolean };
};

type DashboardData = {
  syncStatus: {
    vrsApi: SyncInfo | null;
    cdrImport: SyncInfo | null;
    giacom: SyncInfo | null;
    failedLast24h: number;
  };
  serviceConfig: ServiceConfig;
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function statusTag(status: string | null) {
  if (!status) return <Tag>No data</Tag>;
  if (status === 'completed' || status === 'success')
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        OK
      </Tag>
    );
  if (status === 'failed' || status === 'error')
    return (
      <Tag color="red" icon={<CloseCircleOutlined />}>
        Failed
      </Tag>
    );
  if (status === 'started')
    return (
      <Tag color="blue" icon={<ClockCircleOutlined />}>
        Running
      </Tag>
    );
  return <Tag>{status}</Tag>;
}

function Dashboard() {
  const auth = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.authenticated || !auth.token || auth.isLoading) return;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const result = await ApiClient.get<DashboardData>('/admin/dashboard', controller.signal);
        setData(result);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    void load();
    return () => controller.abort();
  }, [auth.authenticated, auth.token, auth.isLoading]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return <Alert message={error || 'No data'} type="error" showIcon />;
  }

  const { syncStatus, serviceConfig } = data;

  const cards = [
    {
      title: 'Data API Sync',
      subtitle: 'Data SIM card details',
      time: syncStatus.vrsApi?.completed_at || syncStatus.vrsApi?.started_at || null,
      status: syncStatus.vrsApi?.status || null,
      icon: <SyncOutlined />,
    },
    {
      title: 'CDR Import',
      subtitle: 'Usage data from files',
      time: syncStatus.cdrImport?.completed_at || syncStatus.cdrImport?.started_at || null,
      status: syncStatus.cdrImport?.status || null,
      icon: <CloudDownloadOutlined />,
    },
    {
      title: 'Crew Sync',
      subtitle: 'Crew SIM phone data',
      time: (syncStatus.giacom?.created_at as string) || null,
      status: syncStatus.giacom?.status || null,
      icon: <SyncOutlined />,
    },
    {
      title: 'Errors (24h)',
      subtitle: 'Failed sync operations',
      time: null,
      status: null,
      icon: <WarningOutlined />,
      value: syncStatus.failedLast24h,
      isError: true,
    },
  ];

  return (
    <>
      <Row align="middle" style={{ marginBottom: 16 }}>
        <Col flex="none">
          <PageHelp
            title="Operations Dashboard"
            sections={[
              {
                title: 'What is this?',
                content:
                  'A quick overview of the automated services that keep the portal data current.\n\nIf any card shows "Failed" or the error count is red, check the Sync History page for details.',
              },
              {
                title: 'Services',
                content:
                  'Data API Sync: Updates Data SIM card details (status, alias, product) from KORE IoT every hour.\n\nCDR Import: Imports call/data usage records from CDR files stored in S3, triggered daily.\n\nCrew Sync: Downloads Crew SIM phone assignment data daily.',
              },
              {
                title: 'Schedules',
                content:
                  'Data API: Every hour\nData SFTP (reports + CDR files): Daily at 08:00 UTC\nCrew SFTP: Daily at 00:30 UTC\nCrew API: Daily at 01:00 UTC',
              },
            ]}
          />
        </Col>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>
            Operations Dashboard
          </Title>
        </Col>
      </Row>

      {/* Sync Status Cards — equal height */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {cards.map(card => (
          <Col xs={12} sm={6} key={card.title}>
            <Card
              size="small"
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              bodyStyle={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Statistic
                title={card.title}
                value={card.isError ? card.value : timeAgo(card.time)}
                prefix={card.icon}
                valueStyle={{
                  fontSize: 16,
                  ...(card.isError ? { color: (card.value ?? 0) > 0 ? '#ff4d4f' : '#52c41a' } : {}),
                }}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>{card.subtitle}</div>
              {card.status && <div style={{ marginTop: 4 }}>{statusTag(card.status)}</div>}
            </Card>
          </Col>
        ))}
      </Row>

      {/* Scheduled Tasks */}
      {serviceConfig?.schedules?.length > 0 && (
        <Card
          title={
            <>
              <SettingOutlined /> Scheduled Tasks
            </>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table
            size="small"
            pagination={false}
            rowKey="name"
            dataSource={serviceConfig.schedules}
            columns={[
              { title: 'Task', dataIndex: 'name', key: 'name' },
              {
                title: 'Schedule',
                dataIndex: 'schedule',
                key: 'schedule',
                render: (s: string) => {
                  const fmt = formatAwsSchedule(s);
                  if (!fmt) return '—';
                  return (
                    <div>
                      <div>{fmt.human}</div>
                      <Tooltip title="Raw EventBridge expression">
                        <Tag style={{ marginTop: 4 }}>{fmt.raw}</Tag>
                      </Tooltip>
                    </div>
                  );
                },
              },
              {
                title: 'Next run',
                key: 'nextRun',
                width: 220,
                render: (_: unknown, row: ScheduleInfo) => {
                  const fmt = formatAwsSchedule(row.schedule);
                  if (!fmt || !fmt.nextRunUtc) {
                    return <span style={{ color: '#999' }}>—</span>;
                  }
                  return (
                    <div style={{ fontSize: 12 }}>
                      <div>{formatUtcDateTime(fmt.nextRunUtc)}</div>
                      <div style={{ color: '#888' }}>{formatTimeUntil(fmt.nextRunUtc)}</div>
                    </div>
                  );
                },
              },
              {
                title: 'Status',
                dataIndex: 'state',
                key: 'state',
                width: 90,
                render: (s: string) => <Tag color={s === 'ENABLED' ? 'green' : 'red'}>{s}</Tag>,
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
              },
            ]}
          />
        </Card>
      )}

      {/* Lambda Functions */}
      {serviceConfig?.lambdas?.length > 0 && (
        <Card
          title={
            <>
              <SettingOutlined /> Lambda Functions
            </>
          }
          size="small"
        >
          <Table
            size="small"
            pagination={false}
            rowKey="name"
            dataSource={serviceConfig.lambdas}
            columns={[
              { title: 'Function', dataIndex: 'name', key: 'name' },
              { title: 'Runtime', dataIndex: 'runtime', key: 'runtime', width: 100 },
              {
                title: 'Memory',
                dataIndex: 'memory',
                key: 'memory',
                width: 80,
                render: (m: number) => `${m} MB`,
              },
              {
                title: 'Timeout',
                dataIndex: 'timeout',
                key: 'timeout',
                width: 80,
                render: (t: number) => `${t}s`,
              },
              {
                title: 'AUTHZ',
                dataIndex: 'authzEnabled',
                key: 'authz',
                width: 80,
                render: (v: string, row: LambdaInfo) => {
                  // admin Lambda always enforces RBAC via authorize.js, no feature flag needed
                  if (row.name === 'admin') return <Tag color="green">Always ON</Tag>;
                  if (v === 'true') return <Tag color="green">ON</Tag>;
                  if (v === 'not set') return <Tag color="red">OFF</Tag>;
                  return <Tag>{v}</Tag>;
                },
              },
              {
                title: 'Last Deploy',
                dataIndex: 'lastModified',
                key: 'lastModified',
                render: (d: string) => (d ? new Date(d).toLocaleDateString() : '—'),
              },
            ]}
          />
        </Card>
      )}
    </>
  );
}

export default Dashboard;
